/**
 * Export Utilities
 * Functions for exporting charts and data as CSV/PDF
 */

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { saveAs } from 'file-saver'

/**
 * Convert data array to CSV format
 */
const convertToCSV = (data: any[]): string => {
  if (!data || data.length === 0) return ''

  const headers = Object.keys(data[0])
  const rows = data.map((row) =>
    headers.map((header) => {
      const value = row[header]
      // Escape values containing commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    })
  )

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
}

/**
 * Export data to CSV file
 */
export const exportToCSV = (data: any[], filename: string): void => {
  try {
    const csv = convertToCSV(data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const timestamp = new Date().toISOString().split('T')[0]
    saveAs(blob, `${filename}-${timestamp}.csv`)
  } catch (error) {
    console.error('Error exporting CSV:', error)
    throw new Error('Falha ao exportar CSV')
  }
}

/**
 * Capture HTML element as image and export to PDF
 */
export const exportToPDF = async (elementId: string, filename: string): Promise<void> => {
  try {
    const element = document.getElementById(elementId)
    if (!element) {
      throw new Error(`Element with id "${elementId}" not found`)
    }

    // Show loading state (optional - caller should handle)
    console.log('Capturing element as image...')

    // Capture element as canvas
    const canvas = await html2canvas(element, {
      scale: 2, // Higher quality
      backgroundColor: '#232429', // Match dark theme
      logging: false,
      useCORS: true
    })

    const imgData = canvas.toDataURL('image/png')

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    // Calculate dimensions
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = pdfWidth - 20 // 10mm margin each side
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    // Add image to PDF (handle multiple pages if needed)
    let heightLeft = imgHeight
    let position = 10

    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
    heightLeft -= pdfHeight - 20 // Account for margins

    // Add additional pages if content is taller than one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
      heightLeft -= pdfHeight - 20
    }

    // Add footer with timestamp and user info
    const pageCount = pdf.internal.pages.length - 1 // Subtract the first empty page
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i)
      pdf.setFontSize(8)
      pdf.setTextColor(150)
      pdf.text(
        `Gerado em ${new Date().toLocaleString('pt-BR')} | Página ${i} de ${pageCount}`,
        10,
        pdf.internal.pageSize.getHeight() - 10
      )
    }

    // Save PDF
    const timestamp = new Date().toISOString().split('T')[0]
    pdf.save(`${filename}-${timestamp}.pdf`)

    console.log('PDF exported successfully')
  } catch (error) {
    console.error('Error exporting PDF:', error)
    throw new Error('Falha ao exportar PDF')
  }
}

/**
 * Capture specific chart element as PNG image
 */
export const captureChartImage = async (chartRef: HTMLElement): Promise<string> => {
  try {
    const canvas = await html2canvas(chartRef, {
      scale: 2,
      backgroundColor: '#232429',
      logging: false,
      useCORS: true
    })

    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('Error capturing chart image:', error)
    throw new Error('Falha ao capturar imagem do gráfico')
  }
}

/**
 * Copy data to clipboard (fallback for failed exports)
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
    console.log('Data copied to clipboard')
  } catch (error) {
    console.error('Error copying to clipboard:', error)
    throw new Error('Falha ao copiar para área de transferência')
  }
}

/**
 * Format data for export
 * Handles dates, numbers, and nested objects
 */
export const formatDataForExport = (data: any[]): any[] => {
  return data.map((item) => {
    const formatted: any = {}

    Object.keys(item).forEach((key) => {
      const value = item[key]

      // Handle dates
      if (value instanceof Date) {
        formatted[key] = value.toLocaleDateString('pt-BR')
      }
      // Handle numbers (format currency if value is large)
      else if (typeof value === 'number') {
        formatted[key] = value
      }
      // Handle nested objects (flatten)
      else if (typeof value === 'object' && value !== null) {
        formatted[key] = JSON.stringify(value)
      }
      // Handle everything else
      else {
        formatted[key] = value
      }
    })

    return formatted
  })
}
